import { useQuery } from '@apollo/client';
import { Button, Chip } from '@smithhealth/design-system';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DrugAutocomplete } from '@components/drug-autocomplete/drug-autocomplete';
import {
  Table,
  type ColumnDefinition,
  type SortDescriptor,
} from '@components/sds/table';
import { useSelectedPharmacy } from '@services/pharmacy';
import { OrderDirectionEnum } from 'generated-schema-types';
import type {
  MacListEntry,
  MacListOrder,
  PharmacyV2,
} from 'generated-schema-types';
import styles from './mac-list.module.scss';
import SEARCH_MAC from './search-mac.graphql';

const PAGE_SIZE = 10;
interface PharmacyMacSearchData {
  pharmacy: PharmacyV2 | null;
}

export type ActiveMacEntrySortField = 'type' | 'drugPrice' | 'expirationDate';

export interface ActiveMacEntrySortDescriptor {
  field: ActiveMacEntrySortField;
  direction: OrderDirectionEnum;
}

const columns: ColumnDefinition<MacListEntry>[] = [
  {
    field: 'type',
    header: 'Type',
    valueGetter: ({ gpiMatch, ndcMatch }) => {
      if (ndcMatch) {
        return 'NDC';
      }

      if (gpiMatch) {
        return 'GPI';
      }

      return null;
    },
    sortable: true,
  },
  {
    field: 'value',
    header: 'Value',
    valueGetter: ({ gpiMatch, ndcMatch }) => gpiMatch ?? ndcMatch,
  },
  {
    field: 'drugDescription',
    header: 'Description',
  },
  {
    field: 'drugPrice',
    header: 'MAC Price',
    valueFormatter: value => (value ? `$${(value as number).toFixed(2)}` : ''),
    sortable: true,
  },
  {
    field: 'effectiveDate',
    header: 'Effective From',
    sortable: true,
    valueFormatter: value =>
      value
        ? (value as string).replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')
        : '-',
  },
  {
    field: 'expirationDate',
    header: 'Effective To',
    sortable: true,
    valueFormatter: value =>
      value && !(value as string).startsWith('2222')
        ? (value as string).replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1')
        : '-',
  },
];

export const MacList = () => {
  const { selectedPharmacy } = useSelectedPharmacy();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | undefined>();
  const [sortDescriptor, setSortDescriptor] =
    useState<ActiveMacEntrySortDescriptor>({
      field: 'type',
      direction: OrderDirectionEnum.Asc,
    });

  const drugname = searchParams.get('drugName');
  const ndc = searchParams.get('ndc');
  const gpi = searchParams.get('gpi');

  const clearDrugname = () =>
    setSearchParams(params => {
      params.delete('drugName');

      return params;
    });
  const clearGpi = () =>
    setSearchParams(params => {
      params.delete('gpi');

      return params;
    });
  const clearNdc = () =>
    setSearchParams(params => {
      params.delete('ndc');

      return params;
    });
  const formatWhere = () => ({
    drugName: drugname ? { ilike: drugname } : undefined,
    drugCode: { in: [ndc, gpi].filter(Boolean) },
  });

  const onSearch = () => {
    if (selected?.startsWith('ndc')) {
      setSearchParams(params => {
        params.set('ndc', selected?.substring(4));

        return params;
      });
    }

    if (selected?.startsWith('gpi')) {
      setSearchParams(params => {
        params.set('ndc', selected?.substring(4));

        return params;
      });
    }

    if (selected?.startsWith('drugName')) {
      setSearchParams(params => {
        params.set('drugName', selected?.substring(9));

        return params;
      });
    }

    setSelected(undefined);
  };

  const handleSort = (sort: SortDescriptor) => {
    // Convert table sort descriptor to pharmacy sort descriptor
    const field = sort.column as 'type' | 'drugPrice' | 'expirationDate';

    // If clicking on the already sorted column, toggle direction
    let direction: OrderDirectionEnum;

    if (field === sortDescriptor.field) {
      direction =
        sortDescriptor.direction === OrderDirectionEnum.Asc
          ? OrderDirectionEnum.Desc
          : OrderDirectionEnum.Asc;
    } else {
      // New column, default to ASC
      direction = OrderDirectionEnum.Asc;
    }

    setSortDescriptor({ field, direction });
  };

  // Convert pharmacy sort descriptor to table sort descriptor
  const tableSortDescriptor: SortDescriptor = {
    column: sortDescriptor.field,
    direction: sortDescriptor.direction as 'ASC' | 'DESC',
  };

  const order: MacListOrder = useMemo(
    () => ({
      [sortDescriptor.field]: sortDescriptor.direction,
    }),
    [sortDescriptor.field, sortDescriptor.direction]
  );

  const { data, refetch, loading } = useQuery<PharmacyMacSearchData>(
    SEARCH_MAC,
    {
      variables: {
        first: PAGE_SIZE,
        pharmacyId: selectedPharmacy?.id,
        where: formatWhere(),
        order,
      },
      skip: !selectedPharmacy?.id,
    }
  );

  const pharmacy = data?.pharmacy;
  const pageInfo = pharmacy?.activeMacListEntries?.pageInfo;

  const onPreviousPage = async () => {
    await refetch({
      first: undefined,
      after: undefined,
      before: pageInfo?.startCursor,
      last: PAGE_SIZE,
    });
  };

  const onNextPage = async () => {
    await refetch({
      first: PAGE_SIZE,
      after: pageInfo?.endCursor,
      before: undefined,
      last: undefined,
    });
  };

  const macs =
    pharmacy?.activeMacListEntries?.edges?.map(({ node }) => node) ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <DrugAutocomplete setSelected={setSelected} selected={selected} />
        <Button
          hierarchy="primary"
          variant="brand"
          icon="search"
          iconPosition="start"
          onClick={onSearch}
        >
          Search
        </Button>
      </div>

      <div className={styles.results}>
        {(drugname || ndc || gpi) && (
          <div className={styles.activeFiltersHeader}>
            <div className={styles.filtersLabel}>Filtered By</div>
            {drugname && (
              <Chip
                label={drugname}
                variant="brand"
                onDelete={() => clearDrugname()}
              />
            )}

            {gpi && (
              <Chip label={gpi} variant="brand" onDelete={() => clearGpi()} />
            )}

            {ndc && (
              <Chip label={ndc} variant="brand" onDelete={() => clearNdc()} />
            )}
          </div>
        )}
        <Table
          stickyHeader={true}
          isLoading={loading}
          getRowId={({ id }) => id}
          data={macs}
          columns={columns}
          onSort={handleSort}
          sortDescriptor={tableSortDescriptor}
        />
        <div className={styles.pagination}>
          <Button
            hierarchy="secondary"
            variant="brand"
            size="small"
            onClick={onPreviousPage}
            disabled={!pageInfo?.hasPreviousPage}
          >
            Previous
          </Button>
          <Button
            hierarchy="secondary"
            variant="brand"
            size="small"
            onClick={onNextPage}
            disabled={!pageInfo?.hasNextPage}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
